/**
 * state.js — Application State Management
 *
 * PURPOSE:
 *   Central state store for the widget. Manages tenant config, session,
 *   user auth, chat state, cart, and speech status.
 *
 * HOW IT WORKS:
 *   In-memory state object (not persisted). getState() returns reference,
 *   setState() merges updates.
 *
 * BE DEPENDENCY:
 *   None — all state is client-side
 *
 * USAGE:
 *   import { getState, setState } from './core/state.js';
 */

export const DEFAULT_TENANT_CONFIG = {
  tenant_id: null,
  display_name: "",
  theme: {
    primary_color: "#2C2C2C",
    accent_color: "#8C8C8C",
    selected_chip_color: null,
    text_color: "#1A1A1A",
    background_color: "#FFFFFF",
    font_family: "Open Sans, sans-serif",
    font_url: null,                    // optional extra webfont stylesheet (core/fonts.js)
    bot_name: "Concierge",
    button_text: "Ask Concierge",
    bot_avatar_url: null,
    user_avatar_url: null,
    banner_image_url: null,
    banner_image_url_alt: null,        // optional 2nd hero — A/B rotate with banner_image_url
    banner_bg_image: null,
    banner_curve_image_url: null,     // optional decorative curve along the promo strip bottom
    banner: null,                      // optional rich banner: { image_url, text, cta_label, cta_url }
    promo_poster: null,                // optional: { image_url, alt, link_url, clickable }
    chat_background_url: null,
    chat_background: null,
    header_icon_url: null,
    welcome_message: "Hello! How can I help you today?",
    welcome: {
      show_info_icon: false,
      info_text: null,
    },
    error_message: "Sorry, I'm unable to respond right now. Please try again shortly.",
    waiting_gif_url: null,
    mic_icon_url: null,
    feedback_url: null,
    account_login_url: null,           // Fogarty: header login dropdown target
    account_register_url: null,        // Fogarty: header register dropdown target
  },
  contact: {
    phone: null,
    email: null,
    address: null,
    website: null,
  },
  commerce7: {
    // Publishable (not secret) Stripe key — tokenizes a brand-new card for
    // club-signup payment (features/optional/stripe_c7.js). Per-tenant since
    // each Commerce7 winery has its own Stripe account.
    stripe_publishable_key: null,
  },
  ui_features: {
    show_banner: true,
    show_search_bar: true,
    auto_open: false,
    auto_open_first_visit: false,      // homepage, once per visit, 2s delay
    password_protected: false,
    password: null,
    show_promo_bar: false,
    promo_url: null,
    show_mic: true,
    show_feedback: true,
    show_rating: true,
    is_event: false,                   // gates Events entries in guided_flow_questions
    show_cart: false,                  // Fogarty-only: header cart icon
    show_user_account: false,          // Fogarty-only: header login dropdown
  },
  features: {
    commerce7: false,
    shopify: false,
    redchirp: false,
    klaviyo: false,
    live_chat: false,
    where_to_buy: false,
    reservations: false,
  },
  guided_flow_questions: [],
  // Host-page search bar (features/optional/search-bar.js). Like
  // guided_flow_questions this cannot fit the BE's string-only store, so it
  // arrives as ONE JSON-encoded string and normalize.js parses it.
  // null = the winery has no search bar (the default for every tenant).
  search_bar: null,
};

const DEFAULT_STATE = {
  tenant: null,
  session: {
    id: null,
    entryPoint: null,
    tableNo: null,
    isQR: false,
  },
  user: {
    isAuthenticated: false,
    customerId: null,
    email: null,
  },
  chat: {
    isOpen: false,
    isDisabled: false,
    isStreaming: false,
    messages: [],
    conversationHistory: [],
  },
  cart: {
    id: null,
    items: [],
    isVisible: false,
    emptyMessage: ""
  },
  speech: {
    isListening: false,
    isAvailable: false,
  },
};

let currentState = { ...DEFAULT_STATE }

const listeners = {};

export function on(event, callback) {
  if (!listeners[event]) listeners[event] = [];
  listeners[event].push(callback);
}

export function emit(event, data) {
  (listeners[event] || []).forEach(cb => cb(data));
}

export function getState() {
  return currentState;
}

export function getStateSnapshot() {
  return currentState;
}

export function setState(updates) {
  currentState = { ...currentState, ...updates };
}

export function updateChatState(updates) {
  currentState.chat = { ...currentState.chat, ...updates };
}

export function isChatOpen() {
  return currentState.chat.isOpen;
}

export function isChatDisabled(){
  return currentState.chat.isDisabled;
}

let _msgIdCounter = 0;

export function addMessage(message) {
  const enriched = {
    id: message.id || `m_${Date.now()}_${++_msgIdCounter}`,
    role: message.role || "bot",
    kind: message.kind || "text",
    isFirstResponse: message.isFirstResponse ?? true,
    data: message.data || {},
    meta: message.meta || {},
    timestamp: message.timestamp || Date.now(),
  };
  currentState.chat.messages.push(enriched);
  emit("ws-message-added", enriched);
  return enriched;
}

// Remove a specific earlier message (both the DOM node, via MessageList's
// ws-message-removed listener, and the persisted array — so it doesn't
// reappear on a reload/history replay). Used to clear stale login/logout
// prompts once the opposite transition happens (old skipLoginForm parity).
export function removeMessage(id) {
  const idx = currentState.chat.messages.findIndex((m) => m.id === id);
  if (idx === -1) return;
  currentState.chat.messages.splice(idx, 1);
  emit("ws-message-removed", { id });
}

// Patch an existing message's data in place (used for token-by-token streaming).
export function updateMessage(id, dataPatch) {
  const msg = currentState.chat.messages.find((m) => m.id === id);
  if (!msg) return;
  msg.data = { ...msg.data, ...dataPatch };
  emit("ws-message-updated", { id, data: msg.data });
}

export function clearMessages() {
  currentState.chat.messages = [];
  emit("ws-messages-cleared");
}

export function getMessages() {
  return currentState.chat.messages;
}

// Replace the whole transcript in one shot. Used to rehydrate persisted
// messages at boot (see core/chat-history.js) before MessageList renders, so
// the restored conversation shows instead of a freshly seeded welcome.
// Does not emit — callers run this before any listener is attached.
export function setMessages(messages) {
  currentState.chat.messages = Array.isArray(messages) ? messages : [];
}


export function clearLocalStorage(keys=[]){
  if(!keys.length) return;
  keys.forEach((key)=>{
    localStorage.removeItem(key);
  })
}
